import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { authHeader, clearToken, isAuthed } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  rectSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Upload, Link2, Trash2, LogOut, Image as ImgIcon, Plus,
  FolderOpen, Pencil, Check, X, GripVertical, Star, Home, Wrench,
} from "lucide-react";
import { SERVICES } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BE = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (u) => (u?.startsWith("http") ? u : `${BE}${u}`);
const UNCATEGORIZED = "__uncat__";

export default function AdminGallery() {
  const nav = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [mode, setMode] = useState("upload");
  const [title, setTitle] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [newAlbumTitle, setNewAlbumTitle] = useState("");
  const [editAlbumId, setEditAlbumId] = useState(null);
  const [editAlbumTitle, setEditAlbumTitle] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!isAuthed()) { nav("/admin"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handle401 = (err) => {
    if (err?.response?.status === 401) {
      clearToken(); toast.error("Session expired"); nav("/admin"); return true;
    }
    return false;
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [al, gi] = await Promise.all([
        axios.get(`${API}/admin/albums`, { headers: authHeader() }),
        axios.get(`${API}/gallery`),
      ]);
      setAlbums(al.data); setItems(gi.data);
    } catch (err) { if (!handle401(err)) toast.error("Could not load data"); }
    finally { setLoading(false); }
  };

  const loadItems = async () => {
    try { const { data } = await axios.get(`${API}/gallery`); setItems(data); }
    catch (err) { handle401(err); }
  };

  const loadAlbums = async () => {
    try { const { data } = await axios.get(`${API}/admin/albums`, { headers: authHeader() }); setAlbums(data); }
    catch (err) { handle401(err); }
  };

  // ---------- Album actions ----------
  const createAlbum = async () => {
    const t = newAlbumTitle.trim(); if (!t) return;
    try {
      const { data } = await axios.post(`${API}/admin/albums`, { title: t }, { headers: authHeader() });
      setAlbums([...albums, data]); setActiveAlbumId(data.id);
      setNewAlbumTitle(""); setShowNewAlbum(false); toast.success(`Album "${data.title}" created`);
    } catch (err) { if (!handle401(err)) toast.error("Could not create album"); }
  };

  const renameAlbum = async (id) => {
    const t = editAlbumTitle.trim(); if (!t) { setEditAlbumId(null); return; }
    try {
      const { data } = await axios.put(`${API}/admin/albums/${id}`, { title: t }, { headers: authHeader() });
      setAlbums(albums.map(a => a.id === id ? data : a)); setEditAlbumId(null); toast.success("Renamed");
    } catch (err) { if (!handle401(err)) toast.error("Rename failed"); }
  };

  const deleteAlbum = async (id) => {
    const alb = albums.find(a => a.id === id);
    if (!confirm(`Delete album "${alb?.title}"? Photos stay in "Uncategorized".`)) return;
    try {
      await axios.delete(`${API}/admin/albums/${id}`, { headers: authHeader() });
      setAlbums(albums.filter(a => a.id !== id));
      if (activeAlbumId === id) setActiveAlbumId(null);
      await loadItems(); toast.success("Album removed");
    } catch (err) { if (!handle401(err)) toast.error("Delete failed"); }
  };

  const toggleFeatureHome = async (id) => {
    const alb = albums.find(a => a.id === id);
    const nextVal = !alb.is_featured_on_home;
    try {
      await axios.put(`${API}/admin/albums/${id}`, { is_featured_on_home: nextVal }, { headers: authHeader() });
      await loadAlbums();
      toast.success(nextVal ? `"${alb.title}" is now featured on Home` : "Removed from Home");
    } catch (err) { if (!handle401(err)) toast.error("Failed"); }
  };

  const setAlbumCover = async (albumId, itemId) => {
    try {
      await axios.put(`${API}/admin/albums/${albumId}`, { cover_item_id: itemId }, { headers: authHeader() });
      await loadAlbums(); toast.success("Cover updated");
    } catch (err) { if (!handle401(err)) toast.error("Failed"); }
  };

  const linkAlbumToService = async (albumId, serviceSlug) => {
    // serviceSlug === "" clears the link (send null)
    const payload = { service_slug: serviceSlug || null };
    try {
      await axios.put(`${API}/admin/albums/${albumId}`, payload, { headers: authHeader() });
      await loadAlbums();
      if (serviceSlug) {
        const svc = SERVICES.find(s => s.slug === serviceSlug);
        toast.success(`Album linked to "${svc?.title || serviceSlug}"`);
      } else {
        toast.success("Service link removed");
      }
    } catch (err) { if (!handle401(err)) toast.error("Link failed"); }
  };

  const onDragEndAlbums = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = albums.findIndex(a => a.id === active.id);
    const newIdx = albums.findIndex(a => a.id === over.id);
    const next = arrayMove(albums, oldIdx, newIdx);
    setAlbums(next);
    try {
      await axios.post(`${API}/admin/albums/reorder`, { ids: next.map(a => a.id) }, { headers: authHeader() });
    } catch (err) { if (!handle401(err)) toast.error("Reorder failed"); }
  };

  // ---------- Photo actions ----------
  // Client-side downscale — cuts iPhone photos (10+ MB HEIC/JPEG) to ~1-2 MB
  // before upload so we don't time out at Cloudflare/ingress with a huge multipart body.
  const compressImage = (file) => new Promise((resolve, reject) => {
    // Only auto-compress if larger than ~2 MB. Skip HEIC — browser canvas can't decode it (backend handles it).
    if (file.size <= 2 * 1024 * 1024) return resolve(file);
    if (/heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name)) return resolve(file);
    const MAX = 2400;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const ratio = Math.min(MAX / width, MAX / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Compression failed"));
        // Wrap Blob in a File so FormData preserves the filename & type on the wire
        const compressed = new File([blob], file.name.replace(/\.(png|jpg|jpeg|webp)$/i, ".jpg"), { type: "image/jpeg" });
        resolve(compressed);
      }, "image/jpeg", 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };  // fall back to original
    img.src = url;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      const targetAlbum = (activeAlbumId && activeAlbumId !== UNCATEGORIZED) ? activeAlbumId : null;
      if (mode === "upload") {
        if (!file) { toast.error("Choose an image file"); setBusy(false); return; }
        const t = toast.loading(file.size > 2 * 1024 * 1024 ? "Compressing large photo..." : "Uploading...");
        const uploadFile = await compressImage(file);
        toast.dismiss(t);
        const uploading = toast.loading("Uploading...");
        const fd = new FormData();
        fd.append("title", title);
        if (tag) fd.append("tag", tag);
        fd.append("file", uploadFile);
        if (description.trim()) fd.append("description", description.trim());
        if (targetAlbum) fd.append("album_id", targetAlbum);
        await axios.post(`${API}/admin/gallery/upload`, fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
          timeout: 120000, // 2 min so a slow network doesn't cut a legit upload
        });
        toast.dismiss(uploading);
      } else {
        if (!imageUrl) { toast.error("Image URL required"); setBusy(false); return; }
        await axios.post(`${API}/admin/gallery/url`, { title, tag: tag || null, description: description.trim() || null, image_url: imageUrl, album_id: targetAlbum }, { headers: authHeader() });
      }
      toast.success("Photo added");
      setTitle(""); setTag(""); setDescription(""); setImageUrl(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadItems();
    } catch (err) {
      if (handle401(err)) { setBusy(false); return; }
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (err?.code === "ECONNABORTED") {
        toast.error("Upload timed out. Try a smaller photo or retry on a stronger connection.");
      } else if (!err?.response) {
        toast.error("Network error while uploading. Please try again — if it keeps failing, use a smaller photo.");
      } else {
        toast.error(`Upload failed (${err.response.status}). Try a smaller photo.`);
      }
    } finally { setBusy(false); }
  };

  const removePhoto = async (id) => {
    if (!confirm("Remove this photo from the gallery?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeader() });
      setItems(items.filter(i => i.id !== id));
      toast.success("Removed");
    } catch (err) { if (!handle401(err)) toast.error("Delete failed"); }
  };

  const moveToAlbum = async (id, newAlbumId) => {
    try {
      await axios.put(`${API}/admin/gallery/${id}`, { album_id: newAlbumId || "" }, { headers: authHeader() });
      toast.success("Moved");
      await Promise.all([loadItems(), loadAlbums()]);
    } catch (err) { if (!handle401(err)) toast.error("Move failed"); }
  };

  const filteredItems = () => {
    if (activeAlbumId === null) return items;
    if (activeAlbumId === UNCATEGORIZED) return items.filter(i => !i.album_id);
    return items.filter(i => i.album_id === activeAlbumId);
  };
  const visible = filteredItems();

  const onDragEndPhotos = async (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIdx = visible.findIndex(i => i.id === active.id);
    const newIdx = visible.findIndex(i => i.id === over.id);
    const next = arrayMove(visible, oldIdx, newIdx);
    // merge back into items
    const nextItems = items.filter(i => !next.find(n => n.id === i.id)).concat(next);
    setItems(nextItems);
    try {
      await axios.post(`${API}/admin/gallery/reorder`, { ids: next.map(i => i.id) }, { headers: authHeader() });
    } catch (err) {
      if (!handle401(err)) toast.error("Reorder failed");
      await loadItems();
    }
  };

  const signOut = () => { clearToken(); nav("/admin"); };

  const activeAlbum = albums.find(a => a.id === activeAlbumId);
  const countAll = items.length;
  const countUncat = items.filter(i => !i.album_id).length;
  const countByAlbum = (id) => items.filter(i => i.album_id === id).length;

  return (
    <div className="section px-6 lg:px-10" data-testid="admin-gallery-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="eyebrow mb-3">Admin · Gallery</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Manage Photos</h1>
          <div className="mt-3 flex flex-wrap gap-3 items-center text-sm text-zinc-400">
            <Link to="/admin/quotes" className="hover:text-[#FF5722] underline decoration-white/20" data-testid="admin-inbox-link">→ Leads &amp; Messages inbox</Link>
            <span className="text-zinc-600">·</span>
            <Link to="/admin/reviews" className="hover:text-[#FF5722] underline decoration-white/20" data-testid="admin-reviews-link">→ Customer reviews</Link>
          </div>
          <p className="text-zinc-400 mt-3 max-w-2xl">Drag photos and albums to reorder. Star an album to feature it on the homepage. Star a photo to make it the album cover.</p>
        </div>
        <button onClick={signOut} className="btn-ghost" data-testid="admin-signout"><LogOut className="w-4 h-4" /> Sign Out</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* ---------- ALBUMS SIDEBAR ---------- */}
        <aside className="lg:col-span-3 space-y-1" data-testid="albums-sidebar">
          <div className="eyebrow mb-3 flex items-center justify-between">
            Albums · Drag to Reorder
            <button onClick={() => setShowNewAlbum(!showNewAlbum)} className="text-[#FF5722] hover:text-white" data-testid="albums-new-toggle">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {showNewAlbum && (
            <div className="border border-white/10 p-3 mb-3 bg-[#0B0B0D]">
              <Input value={newAlbumTitle} onChange={e => setNewAlbumTitle(e.target.value)} placeholder="Album name" onKeyDown={e => e.key === "Enter" && createAlbum()} data-testid="albums-new-title" />
              <div className="flex gap-2 mt-2">
                <button onClick={createAlbum} className="btn-primary text-xs flex-1 justify-center" data-testid="albums-new-submit">Create</button>
                <button onClick={() => { setShowNewAlbum(false); setNewAlbumTitle(""); }} className="btn-ghost text-xs">Cancel</button>
              </div>
            </div>
          )}

          <AlbumStaticRow active={activeAlbumId === null} title="All Photos" count={countAll} onClick={() => setActiveAlbumId(null)} testId="album-row-all" />

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndAlbums}>
            <SortableContext items={albums.map(a => a.id)} strategy={verticalListSortingStrategy}>
              {albums.map((a) => (
                <SortableAlbumRow
                  key={a.id} album={a}
                  active={activeAlbumId === a.id}
                  count={countByAlbum(a.id)}
                  onSelect={() => setActiveAlbumId(a.id)}
                  onRename={() => { setEditAlbumId(a.id); setEditAlbumTitle(a.title); }}
                  onDelete={() => deleteAlbum(a.id)}
                  onToggleFeature={() => toggleFeatureHome(a.id)}
                  editing={editAlbumId === a.id}
                  editValue={editAlbumTitle}
                  onEditChange={setEditAlbumTitle}
                  onEditSave={() => renameAlbum(a.id)}
                  onEditCancel={() => setEditAlbumId(null)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {countUncat > 0 && (
            <AlbumStaticRow active={activeAlbumId === UNCATEGORIZED} title="Uncategorized" count={countUncat} onClick={() => setActiveAlbumId(UNCATEGORIZED)} testId="album-row-uncat" muted />
          )}
        </aside>

        {/* ---------- UPLOAD FORM ---------- */}
        <div className="lg:col-span-4 space-y-4">
          {activeAlbum && (
            <div className="border border-white/10 p-4 bg-[#0B0B0D]" data-testid="album-service-link-panel">
              <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 flex items-center gap-2"><Wrench className="w-3 h-3" /> Show this album under service page</Label>
              <select
                value={activeAlbum.service_slug || ""}
                onChange={(e) => linkAlbumToService(activeAlbum.id, e.target.value)}
                className="w-full bg-transparent border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF5722]"
                data-testid="album-service-link-select"
              >
                <option value="" className="bg-[#0B0B0D]">— Not linked to any service —</option>
                {SERVICES.map(s => (
                  <option key={s.slug} value={s.slug} className="bg-[#0B0B0D]">{s.title}</option>
                ))}
              </select>
              <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
                When linked, this album's cover becomes the hero image on the service card + detail page, and all its photos appear as a gallery below the service description.
              </p>
            </div>
          )}
          <form onSubmit={submit} className="border border-white/10 p-6 bg-[#0B0B0D] h-fit space-y-4" data-testid="admin-gallery-form">
          <div className="text-xs uppercase tracking-widest text-zinc-500">
            Adding to: <span className="text-white">{activeAlbumId === null ? "All (uncategorized)" : activeAlbumId === UNCATEGORIZED ? "Uncategorized" : activeAlbum?.title}</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setMode("upload")} className={`flex-1 py-2 px-3 border font-display uppercase text-xs tracking-widest ${mode === "upload" ? "bg-[#FF5722] border-[#FF5722] text-white" : "border-white/20 text-zinc-300"}`} data-testid="admin-mode-upload"><Upload className="w-3 h-3 inline mr-1" /> Upload</button>
            <button type="button" onClick={() => setMode("url")} className={`flex-1 py-2 px-3 border font-display uppercase text-xs tracking-widest ${mode === "url" ? "bg-[#FF5722] border-[#FF5722] text-white" : "border-white/20 text-zinc-300"}`} data-testid="admin-mode-url"><Link2 className="w-3 h-3 inline mr-1" /> By URL</button>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Production Booth Run" data-testid="admin-title" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Tag / Caption</Label><Input value={tag} onChange={e => setTag(e.target.value)} placeholder="e.g., Powder Coating" data-testid="admin-tag" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Story (optional)</Label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} placeholder="A longer description shown in the lightbox — the job, the substrate, the outcome."
              className="flex w-full rounded-none border border-white/20 bg-transparent px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:ring-offset-0"
              data-testid="admin-description" />
          </div>
          {mode === "upload" ? (
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Photo File</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-zinc-300 file:mr-3 file:py-2 file:px-3 file:border file:border-white/20 file:bg-transparent file:text-white file:font-display file:uppercase file:tracking-widest file:text-xs hover:file:border-[#FF5722] file:cursor-pointer"
                data-testid="admin-file" />
              {file && <div className="text-xs text-zinc-500 mt-2">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
            </div>
          ) : (
            <div><Label className="text-xs uppercase tracking-widest text-zinc-400 mb-1.5 block">Image URL</Label><Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." data-testid="admin-image-url" /></div>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="admin-add-submit">{busy ? "Adding..." : "Add Photo"}</button>
          </form>
        </div>

        {/* ---------- PHOTO GRID ---------- */}
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">
            {activeAlbumId === null ? "All photos" : activeAlbumId === UNCATEGORIZED ? "Uncategorized" : activeAlbum?.title} · {visible.length} photo{visible.length === 1 ? "" : "s"} · Drag to Reorder
          </div>
          {loading ? (
            <div className="text-zinc-500">Loading...</div>
          ) : visible.length === 0 ? (
            <div className="border border-dashed border-white/10 p-12 text-center">
              <ImgIcon className="w-10 h-10 text-zinc-600 mx-auto" />
              <p className="text-zinc-400 mt-4 text-sm">No photos in this view yet. Use the form on the left.</p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndPhotos}>
              <SortableContext items={visible.map(i => i.id)} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-items-list">
                  {visible.map((it, idx) => {
                    const activeAlbumObj = albums.find(a => a.id === it.album_id);
                    const isCover = activeAlbumObj?.cover_item_id === it.id;
                    return (
                      <SortablePhoto
                        key={it.id} item={it} idx={idx} isCover={isCover}
                        albums={albums}
                        onDelete={() => removePhoto(it.id)}
                        onSetCover={() => it.album_id ? setAlbumCover(it.album_id, it.id) : toast.error("Move to an album first to set as cover")}
                        onMove={(albId) => moveToAlbum(it.id, albId)}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Static (non-sortable) row for "All" and "Uncategorized" ----------
const AlbumStaticRow = ({ active, title, count, onClick, testId, muted }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-2 border p-2 text-left transition-colors ${active ? "border-[#FF5722] bg-[#FF5722]/5" : "border-white/10 hover:border-white/30"} ${muted ? "opacity-70" : ""}`}
    data-testid={testId}>
    <FolderOpen className={`w-4 h-4 ${active ? "text-[#FF5722]" : "text-zinc-500"}`} />
    <span className="font-display tracking-tight">{title}</span>
    <span className="ml-auto text-xs text-zinc-500">{count}</span>
  </button>
);

// ---------- Sortable Album Row ----------
const SortableAlbumRow = ({ album, active, count, onSelect, onRename, onDelete, onToggleFeature, editing, editValue, onEditChange, onEditSave, onEditCancel }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: album.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="flex gap-1 p-2 border border-white/10 bg-[#0B0B0D] group">
        <Input value={editValue} onChange={e => onEditChange(e.target.value)} onKeyDown={e => e.key === "Enter" && onEditSave()} autoFocus />
        <button onClick={onEditSave} className="text-[#FF5722] px-2" data-testid={`album-save-${album.id}`}><Check className="w-4 h-4" /></button>
        <button onClick={onEditCancel} className="text-zinc-400 px-2"><X className="w-4 h-4" /></button>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className={`group flex items-center gap-1 border p-2 transition-colors ${active ? "border-[#FF5722] bg-[#FF5722]/5" : "border-white/10 hover:border-white/30"}`}>
      <button {...attributes} {...listeners} className="text-zinc-500 hover:text-white cursor-grab active:cursor-grabbing touch-none" data-testid={`album-drag-${album.id}`} aria-label="Drag to reorder">
        <GripVertical className="w-3 h-3" />
      </button>
      <button onClick={onSelect} className="flex-1 flex items-center gap-2 text-left min-w-0" data-testid={`album-row-${album.id}`}>
        <FolderOpen className={`w-4 h-4 shrink-0 ${active ? "text-[#FF5722]" : "text-zinc-500"}`} />
        <span className="font-display tracking-tight truncate">{album.title}</span>
        {album.is_featured_on_home && <Home className="w-3 h-3 text-[#FF5722] shrink-0" title="Featured on Home" />}
        {album.service_slug && <Wrench className="w-3 h-3 text-[#FF5722] shrink-0" title={`Linked to service: ${album.service_slug}`} />}
        <span className="ml-auto text-xs text-zinc-500">{count}</span>
      </button>
      <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onToggleFeature} className={`p-1 ${album.is_featured_on_home ? "text-[#FF5722]" : "text-zinc-400 hover:text-[#FF5722]"}`} title="Feature on Home" data-testid={`album-feature-${album.id}`}><Home className="w-3 h-3" /></button>
        <button onClick={onRename} className="p-1 text-zinc-400 hover:text-white" title="Rename" data-testid={`album-rename-${album.id}`}><Pencil className="w-3 h-3" /></button>
        <button onClick={onDelete} className="p-1 text-zinc-400 hover:text-[#FF5722]" title="Delete" data-testid={`album-delete-${album.id}`}><Trash2 className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// ---------- Sortable Photo Card ----------
const SortablePhoto = ({ item, idx, isCover, albums, onDelete, onSetCover, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="border border-white/10 relative overflow-hidden bg-[#0B0B0D] group" data-testid={`admin-item-${item.id}`}>
      <div className="aspect-[4/3] overflow-hidden relative">
        <img src={resolveUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" draggable={false} />
        <div className="absolute top-2 left-2 bg-black/80 border border-white/20 px-2 py-0.5 text-xs font-display flex items-center gap-1">
          {isCover && <Star className="w-3 h-3 text-[#FF5722] fill-[#FF5722]" />}
          #{idx + 1}
        </div>
        <button {...attributes} {...listeners} className="absolute top-2 right-2 bg-black/80 border border-white/20 p-1.5 text-white cursor-grab active:cursor-grabbing touch-none" aria-label="Drag to reorder" data-testid={`admin-drag-${item.id}`}>
          <GripVertical className="w-3 h-3" />
        </button>
      </div>
      <div className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-[#FF5722]">{item.tag}</div>
        <div className="font-display text-base tracking-tight mt-0.5 truncate">{item.title}</div>
        {albums.length > 0 && (
          <select value={item.album_id || ""} onChange={e => onMove(e.target.value)}
            className="mt-2 w-full bg-transparent border border-white/10 text-xs text-zinc-300 py-1 px-2 focus:outline-none focus:border-[#FF5722]"
            data-testid={`admin-move-album-${item.id}`}>
            <option value="">— Uncategorized —</option>
            {albums.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        )}
      </div>
      <div className="flex border-t border-white/10 divide-x divide-white/10">
        <button onClick={onSetCover} className={`flex-1 py-2 hover:bg-white/5 transition-colors ${isCover ? "text-[#FF5722]" : "text-zinc-400 hover:text-[#FF5722]"}`} title={isCover ? "Album cover" : "Set as album cover"} data-testid={`admin-cover-${item.id}`}>
          <Star className={`w-4 h-4 mx-auto ${isCover ? "fill-[#FF5722]" : ""}`} />
        </button>
        <button onClick={onDelete} className="flex-1 py-2 text-zinc-400 hover:text-[#FF5722] hover:bg-[#FF5722]/10 transition-colors" title="Delete" data-testid={`admin-delete-${item.id}`}>
          <Trash2 className="w-4 h-4 mx-auto" />
        </button>
      </div>
    </div>
  );
};
